// Kept free of React/page imports so the header can use it without pulling the lazy PLP into its bundle.
import { ProductSearchSort } from "@lib/ShiOneClient";
import { stripGlobalDialogSegment } from "@features/ecommerce/constants/cartRouteSegments";

// Gates the in-app Product Listing Page (App Configuration key).
export const MERGE_LORDS_ENABLE_PRODUCT_LISTING_PAGE_FEATURE_FLAG =
  "merge-lords.enable-product-listing-page";

export const PRODUCT_LISTING_BASE_PATH = "/shop/search";

// Matches /shop/search and multi-segment category slugs (/shop/search/hardware/cables).
export const PRODUCT_LISTING_ROUTE_PATH = `/:prefix?${PRODUCT_LISTING_BASE_PATH}/*`;

export function buildProductSearchPath(keywords: string): string {
  return `${PRODUCT_LISTING_BASE_PATH}?k=${encodeURIComponent(keywords)}`;
}

// Not "product": the global order drawer stack owns that segment and would open its own quick view.
export const PRODUCT_LISTING_DRAWER_SEGMENT = "item";

export interface ProductListingSplat {
  /** Category slug with the product drawer suffix removed. */
  categorySlug: string;
  /** `{productClass}~{productDescriptor}` from `.../item/{id}`, or undefined when no drawer is open. */
  productRouteId: string | undefined;
}

/**
 * Splits the PLP splat (`hardware/cables/item/shicommodity~4567/stock`) into the category
 * slug and the product drawer route id. The drawer suffix is the last `item/{id}` whose id
 * contains `~`, so a category named "item" stays part of the slug. A trailing global dialog
 * segment such as `added-to-cart` is dropped.
 */
export function splitProductDrawerSuffix(
  splat: string | undefined,
): ProductListingSplat {
  const segments = stripGlobalDialogSegment(splat ?? "")
    .split("/")
    .filter(Boolean);
  for (let index = segments.length - 2; index >= 0; index -= 1) {
    if (
      segments[index] === PRODUCT_LISTING_DRAWER_SEGMENT &&
      segments[index + 1].includes("~")
    ) {
      return {
        categorySlug: segments.slice(0, index).join("/"),
        productRouteId: segments[index + 1],
      };
    }
  }
  return { categorySlug: segments.join("/"), productRouteId: undefined };
}

/** Strips any custom-URL prefix and open product drawer so the shi.com redirect keeps the same slug and query. */
export function toShiComProductListingPath(
  pathname: string,
  search: string,
): string {
  const index = pathname.indexOf(PRODUCT_LISTING_BASE_PATH);
  if (index < 0) return `${PRODUCT_LISTING_BASE_PATH}${search}`;
  const { categorySlug } = splitProductDrawerSuffix(
    pathname.slice(index + PRODUCT_LISTING_BASE_PATH.length),
  );
  const path = categorySlug
    ? `${PRODUCT_LISTING_BASE_PATH}/${categorySlug}`
    : PRODUCT_LISTING_BASE_PATH;
  return `${path}${search}`;
}

// Query param names copied from shi.com so links stay interchangeable.
export const PRODUCT_LISTING_KEYWORD_PARAM = "k";
export const PRODUCT_LISTING_SORT_PARAM = "s";
export const PRODUCT_LISTING_FILTERS_PARAM = "d";
// shi.com appends "search within results" keywords to `k`, separated by `;`.
const KEYWORD_SEPARATOR = ";";

const SORT_TO_PARAM: Partial<Record<ProductSearchSort, string>> = {
  [ProductSearchSort.PriceLowToHigh]: "PriceLowToHigh",
  [ProductSearchSort.PriceHighToLow]: "PriceHighToLow",
  [ProductSearchSort.MfgPartNumberAscending]: "MfgPartNum",
};

const PARAM_TO_SORT = new Map(
  Object.entries(SORT_TO_PARAM).map(([sort, param]) => [
    param,
    sort as ProductSearchSort,
  ]),
);

/** Unknown or missing values fall back to Best Match. */
export function parseSortParam(value: string | null): ProductSearchSort {
  return (value && PARAM_TO_SORT.get(value)) || ProductSearchSort.BestMatch;
}

/** Returns undefined for Best Match so the param is left out of the URL. */
export function toSortParam(sort: ProductSearchSort): string | undefined {
  return SORT_TO_PARAM[sort];
}

const HEX_PATTERN = /^(?:[0-9a-fA-F]{2})+$/;

/** Encodes refinement tokens as shi.com's `d` value: semicolon-terminated tokens, UTF-8 bytes as uppercase hex. */
export function encodeRefinementTokens(tokens: readonly string[]): string {
  const bytes = new TextEncoder().encode(
    tokens.map((token) => `${token};`).join(""),
  );
  return Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0").toUpperCase(),
  ).join("");
}

/** Decodes a `d` value into refinement tokens; invalid hex or UTF-8 yields an empty list. */
export function decodeRefinementTokens(value: string | null): string[] {
  if (!value || !HEX_PATTERN.test(value)) {
    return [];
  }

  const bytes = new Uint8Array(value.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Number.parseInt(value.slice(i * 2, i * 2 + 2), 16);
  }

  try {
    return new TextDecoder("utf-8", { fatal: true })
      .decode(bytes)
      .split(";")
      .filter((token) => token.length > 0);
  } catch {
    return [];
  }
}

export interface ProductListingUrlState {
  keyword: string;
  withinKeywords: string[];
  filters: string[];
  categorySlug: string;
  sort: ProductSearchSort;
}

/** Upstream ANDs keywords, so each within-results keyword narrows the original search. */
export function toSearchQuery({
  keyword,
  withinKeywords,
}: Pick<ProductListingUrlState, "keyword" | "withinKeywords">): string {
  return [keyword, ...withinKeywords].filter(Boolean).join(" ");
}

function splitKeywordParam(value: string | null): string[] {
  return (value ?? "")
    .split(KEYWORD_SEPARATOR)
    .map((term) => term.trim())
    .filter(Boolean);
}

export function parseProductListingUrl(
  searchParams: URLSearchParams,
  categorySlug: string | undefined,
): ProductListingUrlState {
  const [keyword = "", ...withinKeywords] = splitKeywordParam(
    searchParams.get(PRODUCT_LISTING_KEYWORD_PARAM),
  );
  return {
    keyword,
    withinKeywords,
    filters: decodeRefinementTokens(
      searchParams.get(PRODUCT_LISTING_FILTERS_PARAM),
    ),
    categorySlug: (categorySlug ?? "").replace(/^\/+|\/+$/g, ""),
    sort: parseSortParam(searchParams.get(PRODUCT_LISTING_SORT_PARAM)),
  };
}

/** The PLP search term for the header search box; empty off the PLP. */
export function getProductListingSearchTerm(
  pathname: string,
  search: string,
): string {
  if (!pathname.includes(PRODUCT_LISTING_BASE_PATH)) return "";
  return parseProductListingUrl(new URLSearchParams(search), undefined).keyword;
}

/** Returns a copy of the params with `s` set, or removed for Best Match. */
export function withSortParam(
  searchParams: URLSearchParams,
  sort: ProductSearchSort,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  const param = toSortParam(sort);
  if (param) {
    next.set(PRODUCT_LISTING_SORT_PARAM, param);
  } else {
    next.delete(PRODUCT_LISTING_SORT_PARAM);
  }
  return next;
}

/** Returns a copy of the params with `d` set, or removed when there are no filters. */
export function withFiltersParam(
  searchParams: URLSearchParams,
  filters: readonly string[],
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  if (filters.length > 0) {
    next.set(PRODUCT_LISTING_FILTERS_PARAM, encodeRefinementTokens(filters));
  } else {
    next.delete(PRODUCT_LISTING_FILTERS_PARAM);
  }
  return next;
}

/** Refinement token shi.com uses for a manufacturer facet value. */
export function toManufacturerFilterToken(manufacturer: string): string | null {
  const name = manufacturer.trim();
  // The token syntax has no escape for quotes or the `;` separator.
  if (!name || /['";]/.test(name)) return null;
  return `mfg:'${name}'`;
}

/** Returns a copy of the params with the manufacturer's filter added to `d`, or null when it is already applied or can't be encoded. */
export function withManufacturerFilter(
  searchParams: URLSearchParams,
  manufacturer: string,
): URLSearchParams | null {
  const token = toManufacturerFilterToken(manufacturer);
  if (!token) return null;
  const filters = decodeRefinementTokens(
    searchParams.get(PRODUCT_LISTING_FILTERS_PARAM),
  );
  if (filters.includes(token)) return null;
  return withFiltersParam(searchParams, [...filters, token]);
}

/** Refinement name of a token, e.g. `mfg` for `mfg:'Belkin'`. */
export function getRefinementName(token: string): string {
  const index = token.indexOf(":");
  return index > 0 ? token.slice(0, index) : token;
}

/** Category tokens (`ctgyN`) are scoped by category navigation, not the facet rail. */
export function isCategoryToken(token: string): boolean {
  return /^ctgy\d*$/i.test(getRefinementName(token));
}

/**
 * Returns a copy of the params with `k` rebuilt shi.com-style: the search term, then each
 * within-results keyword after a `;` (`k=software;dns;bind`). The search term is kept.
 */
export function withWithinKeywordsParam(
  searchParams: URLSearchParams,
  withinKeywords: readonly string[],
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  const [keyword] = splitKeywordParam(
    searchParams.get(PRODUCT_LISTING_KEYWORD_PARAM),
  );
  const value = [keyword, ...withinKeywords.flatMap(splitKeywordParam)]
    .filter(Boolean)
    .join(KEYWORD_SEPARATOR);
  if (value) {
    next.set(PRODUCT_LISTING_KEYWORD_PARAM, value);
  } else {
    next.delete(PRODUCT_LISTING_KEYWORD_PARAM);
  }
  return next;
}

/** Depth of a category token (`ctgy2:'Laptops'` -> 2); 0 when it is not a category token. */
export function getCategoryDepth(token: string): number {
  const match = /^ctgy(\d+)$/i.exec(getRefinementName(token));
  return match ? Number.parseInt(match[1], 10) : 0;
}

/** The deepest category token in the filters, if any. */
export function getCategoryToken(filters: readonly string[]): string | null {
  return (
    filters
      .filter(isCategoryToken)
      .sort((a, b) => getCategoryDepth(b) - getCategoryDepth(a))[0] ?? null
  );
}

/**
 * shi.com URL slug for a category token (`ctgy2:'Hardware > Computers and Tablets'` -> `hardware/computers-and-tablets`).
 * Mirrors the backend `CategorySlugHelper.ToSlug`, which resolves the slug back to the category.
 */
export function toCategorySlug(token: string): string {
  const index = token.indexOf(":");
  const path = (index >= 0 ? token.slice(index + 1) : token).replace(
    /^'+|'+$/g,
    "",
  );
  return path
    .replaceAll("/", "_")
    .replaceAll(" > ", "/")
    .replaceAll(" ", "-")
    .replaceAll("&", "%26")
    .toLowerCase();
}

/** The PLP base path for the current location, keeping any custom-URL prefix but no category slug. */
export function getProductListingBasePath(pathname: string): string {
  const index = pathname.indexOf(PRODUCT_LISTING_BASE_PATH);
  return index >= 0
    ? pathname.slice(0, index + PRODUCT_LISTING_BASE_PATH.length)
    : PRODUCT_LISTING_BASE_PATH;
}

function withQuery(path: string, params: URLSearchParams): string {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * shi.com-style category jump (`/shop/search/hardware/computers-and-tablets`): the category moves into the path.
 * Breadcrumb jumps drop the keyword so the whole category is shown; chip jumps keep it (`keepKeyword`).
 * Facet filters and sort are kept, as on shi.com.
 */
export function buildCategoryPath(
  pathname: string,
  searchParams: URLSearchParams,
  token: string,
  { keepKeyword = false }: { keepKeyword?: boolean } = {},
): string {
  const next = withCategoryFilter(searchParams, null);
  if (!keepKeyword) next.delete(PRODUCT_LISTING_KEYWORD_PARAM);
  return withQuery(
    `${getProductListingBasePath(pathname)}/${toCategorySlug(token)}`,
    next,
  );
}

/** Base PLP URL with the current query; used to drop a category slug the backend could not resolve. */
export function buildBasePath(
  pathname: string,
  searchParams: URLSearchParams,
): string {
  return withQuery(getProductListingBasePath(pathname), searchParams);
}

/** Leaves the category: drops the path slug and any category token in `d`, keeping keywords, facets, and sort. */
export function buildClearCategoryPath(
  pathname: string,
  searchParams: URLSearchParams,
): string {
  return withQuery(
    getProductListingBasePath(pathname),
    withCategoryFilter(searchParams, null),
  );
}

/**
 * Removes the search term: like an empty shi.com search, drops `k` (and with it any
 * within-results keywords) plus the category, keeping facet filters and sort.
 */
export function buildClearSearchTermPath(
  pathname: string,
  searchParams: URLSearchParams,
): string {
  const next = new URLSearchParams(searchParams);
  next.delete(PRODUCT_LISTING_KEYWORD_PARAM);
  return buildClearCategoryPath(pathname, next);
}

/** Returns a copy of the params with the category replaced (or cleared when null); other tokens are kept. */
export function withCategoryFilter(
  searchParams: URLSearchParams,
  token: string | null,
): URLSearchParams {
  const filters = decodeRefinementTokens(
    searchParams.get(PRODUCT_LISTING_FILTERS_PARAM),
  ).filter((t) => !isCategoryToken(t));
  return withFiltersParam(searchParams, token ? [...filters, token] : filters);
}
